import { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Plus, X, Pencil } from 'lucide-react';
import DotIndicator from './DotIndicator';
import ConfirmDialog from './ConfirmDialog';
import ImageDisplay from './ImageDisplay';
import ProfileAttachmentMenu from '../Pages/Profile/ProfileAttachmentMenu';

/** Reusable image carousel with swipe, arrows, dot indicators, and optional add-photo slot */
export default function ImageCarousel({
  images = [],
  editable = false,   // show add/remove/replace controls
  onAdd,              // (file: File) => void
  onRemove,           // (index: number) => void
  onReplace,          // (index: number, file: File) => void
  onImageClick,       // (index: number) => void — opens detail viewer
  onEnterGridEdit,    // () => void — switch to grid edit mode
  initialIndex = 0,   // initial slide index
  max = 6,
}) {
  const [i, setI] = useState(initialIndex);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [replaceIndex, setReplaceIndex] = useState(null);

  // Update index when initialIndex changes
  useEffect(() => {
    setI(initialIndex);
  }, [initialIndex]);

  // Append an "add" slot at the end when editable and under max
  const slots = editable && images.length < max ? [...images, '__add__'] : images;
  const total = Math.max(slots.length, 1);
  const current = slots[i];

  const next = () => setI((p) => Math.min(p + 1, total - 1));
  const prev = () => setI((p) => Math.max(p - 1, 0));

  // Swipe gesture handlers
  const onPointerDown = (e) => { setIsDragging(true); dragStartX.current = e.clientX; };
  const onPointerMove = (e) => { if (isDragging) e.preventDefault(); };
  const onPointerUp = (e) => {
    if (!isDragging) return;
    setIsDragging(false);
    const diff = dragStartX.current - e.clientX;
    if (diff > 50) next();
    if (diff < -50) prev();
  };

  const aspect = 'h-[60vh] min-h-[400px] max-h-[calc(100vh-340px)]';

  const handleImageUploaded = (metadata) => {
    if (replaceIndex !== null) {
      // Replace mode
      onReplace?.(replaceIndex, metadata);
      setReplaceIndex(null);
    } else {
      // Add mode
      onAdd?.(metadata);
    }
    setShowAttachmentMenu(false);
  };

  const handleAddClick = () => {
    setReplaceIndex(null);
    setShowAttachmentMenu(true);
  };

  const handleReplaceClick = (index) => {
    setReplaceIndex(index);
    setShowAttachmentMenu(true);
  };

  return (
    <div
      className={`relative w-full ${aspect} overflow-hidden bg-secondary select-none`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      style={{ touchAction: 'pan-y' }}
    >
      {/* Current slide content */}
      {current && current !== '__add__' ? (
        <ImageDisplay
          key={i}
          imageData={current}
          imageStrategy="cover"
          className="h-full w-full"
          onClick={() => onImageClick?.(i)}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-secondary text-primary">
          {current === '__add__' ? (
            // Add photo slot
            <button onClick={handleAddClick} className="flex flex-col items-center gap-2">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Plus className="h-8 w-8" />
              </div>
              <span className="text-sm font-medium">Add photo</span>
            </button>
          ) : (
            <span className="text-sm text-muted-foreground">No photos yet</span>
          )}
        </div>
      )}

      {/* Line indicators — bottom of image, stop click passthrough */}
      <div className="absolute bottom-2 left-4 right-4 z-10 flex justify-center" onClick={(e) => { e.stopPropagation(); e.preventDefault(); }} onPointerDown={(e) => e.stopPropagation()}>
        <DotIndicator total={slots.length} index={i} onSelect={setI} />
      </div>

      {/* Navigation arrows */}
      {slots.length > 1 && (
        <>
          <button
            onClick={prev}
            disabled={i === 0}
            className="absolute left-2 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-black/40 text-white transition hover:bg-black/60 disabled:opacity-0"
            aria-label="Previous"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={next}
            disabled={i === total - 1}
            className="absolute right-2 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-black/40 text-white transition hover:bg-black/60 disabled:opacity-0"
            aria-label="Next"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}

      {/* Edit controls overlay (replace, remove) — z-20 above indicators */}
      {editable && current && current !== '__add__' && (
        <div className="absolute right-2 bottom-[40px] z-20 flex gap-2">
          {images.length < max && (
            <button onClick={handleAddClick} className="flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow">
              <Plus className="h-4 w-4 text-primary" />
            </button>
          )}
          {onEnterGridEdit ? (
            <button
              onClick={onEnterGridEdit}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow"
              title="Edit all photos"
            >
              <Pencil className="h-4 w-4 text-primary" />
            </button>
          ) : (
            <button onClick={() => handleReplaceClick(i)} className="flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow">
              <Pencil className="h-4 w-4 text-primary" />
            </button>
          )}
          <button
            onClick={() => onRemove?.(i)}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow"
          >
            <X className="h-4 w-4 text-destructive" />
          </button>
        </div>
      )}

      {/* Profile attachment menu */}
      {showAttachmentMenu && (
        <ProfileAttachmentMenu
          onClose={() => setShowAttachmentMenu(false)}
          onImageUploaded={handleImageUploaded}
        />
      )}
    </div>
  );
}
